import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../api/axiosConfig';
import { useModal } from '../contexts/ModalContext';

const PERIODS = [
  { value: 'DAY', label: '매일' },
  { value: 'WEEK', label: '주' },
  { value: 'MONTH', label: '월' },
];

const DAYS = [
  { value: 1, label: '월' },
  { value: 2, label: '화' },
  { value: 3, label: '수' },
  { value: 4, label: '목' },
  { value: 5, label: '금' },
  { value: 6, label: '토' },
  { value: 0, label: '일' },
];

/**
 * 경로를 고르는 방식을 코드 이름 대신 결과로 설명한다.
 *
 * PER_ROUTE / SHARED / ROTATE 를 그대로 두면 아무도 고를 수 없다. 특히
 * '경로마다 각각' 은 주 1회라고 적어도 경로가 둘이면 실제로는 주 2회가 되는데,
 * 여기서 오해가 가장 잦아 결과를 한 줄로 붙여 둔다.
 *
 * 경로 안의 들를 곳 수는 곱해지지 않는다. 다섯 곳을 들르든 한 곳을 들르든 한
 * 회차는 한 회차다.
 */
const POOL_MODES = [
  {
    value: 'PER_ROUTE',
    label: '경로마다 각각',
    hint: (names, q) => `${names.length}개 경로를 각각 ${q}회 → 합쳐서 ${names.length * q}회`,
  },
  {
    value: 'SHARED',
    label: '합쳐서',
    hint: (names, q) => `어느 경로든 ${q}회 채우면 됨`,
  },
  {
    value: 'ROTATE',
    label: '번갈아',
    // '순서대로' 라고만 하면 무엇의 순서인지 알 수 없다. 이름을 늘어놓으면
    // 차례가 그대로 보이고, 경로 순서가 곧 도는 차례라는 것도 함께 드러난다.
    hint: (names) => `한 주기에 한 경로씩 · ${names.join(' → ')} → 다시 처음`,
  },
];

/**
 * 서버가 알아듣는 시간대 이름.
 *
 * 자유 입력이었는데, 서버는 이 넷만 안다. 못 알아들으면 하루 전체로 처리하므로
 * '오전' 이라고 적어 두고 실제로는 종일 도는 일이 생겼다. 고를 수 있게 바꾼다.
 */
const WINDOW_LABELS = [
  { value: 'MORNING', label: '오전' },
  { value: 'AFTERNOON', label: '오후' },
  { value: 'NIGHT', label: '야간' },
  { value: 'ANYTIME', label: '시간 무관' },
];

// 시각을 안 정한 새 줄이 시간대를 조용히 좁히지 않도록 '시간 무관' 에서 시작한다.
const emptyWindow = () => ({ label: 'ANYTIME', name: '', start: '', end: '', count: 1 });
const emptyStop = () => ({ facilityId: '', sectionId: null, templateId: null });
const emptyRoute = () => ({ name: '', stops: [emptyStop()] });

const InspectionPlanFormPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showAlert, showConfirm } = useModal();
  const isNew = !id || id === 'new';

  const [facilities, setFacilities] = useState([]);
  const [templates, setTemplates] = useState([]);
  /** 다른 계획의 경로를 가져오기 위해서만 쓴다. */
  const [plans, setPlans] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [importOpen, setImportOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  /** 끌고 있는 곳과 지금 올라와 있는 자리. { ri, si, over } */
  const [dragging, setDragging] = useState(null);

  const [form, setForm] = useState({
    name: '',
    period: 'WEEK',
    quota: 1,
    windows: [emptyWindow()],
    days: [1, 2, 3, 4, 5],
    poolMode: 'PER_ROUTE',
    graceMinutes: 60,
    status: 'ACTIVE',
    routes: [emptyRoute()],
    assignedWorkerIds: [],
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [facRes, tplRes, planRes, userRes] = await Promise.all([
        api.get('/facilities'),
        api.get('/checklist-templates'),
        api.get('/inspection-plans'),
        api.get('/users'),
      ]);
      setFacilities(facRes.data);
      setTemplates(tplRes.data);
      setPlans(planRes.data);
      // 건물주 계정은 점검을 하지 않는다. 고를 수 있게 두면 잘못 고른다.
      setWorkers((userRes.data || []).filter((u) => u.role !== 'CLIENT'));

      if (!isNew) {
        const { data } = await api.get(`/inspection-plans/${id}`);
        setForm({
          name: data.name,
          period: data.period,
          quota: data.quota,
          windows: (Array.isArray(data.windows) ? data.windows : []).map((w) => ({
            label: w.label ?? 'ANYTIME',
            name: w.name ?? '',
            start: w.start ?? '',
            end: w.end ?? '',
            count: w.count ?? 1,
          })),
          days: data.dayRule?.days ?? [1, 2, 3, 4, 5],
          poolMode: data.poolMode,
          graceMinutes: data.graceMinutes,
          status: data.status,
          assignedWorkerIds: (data.assignedWorkers || []).map((w) => w.id),
          routes: (data.routes || []).map((r) => ({
            name: r.name ?? '',
            stops: (r.stops || []).map((st) => ({
              facilityId: st.facilityId,
              sectionId: st.sectionId ?? null,
              templateId: st.templateId ?? null,
            })),
          })),
        });
      }
    } catch (err) {
      console.error('Failed to load plan form', err);
      showAlert('오류', '불러오지 못했습니다.', 'danger');
    } finally {
      setLoading(false);
    }
  }, [id, isNew, showAlert]);

  useEffect(() => {
    load();
  }, [load]);

  const set = (patch) => setForm((prev) => ({ ...prev, ...patch }));

  const setWindow = (idx, patch) =>
    set({ windows: form.windows.map((w, i) => (i === idx ? { ...w, ...patch } : w)) });

  const setRoute = (ri, patch) =>
    set({ routes: form.routes.map((r, i) => (i === ri ? { ...r, ...patch } : r)) });

  /** 아직 아무것도 안 채운 경로. 가져오기가 이걸 밀어내고 들어간다. */
  const isBlankRoute = (r) =>
    !(r.name || '').trim() && r.stops.every((st) => !st.facilityId);

  /**
   * 다른 계획에 이미 있는 경로를 그대로 들여온다.
   *
   * 예비 순회는 정기순회와 같은 곳을 돈다. 그런데 13곳을 손으로 다시 치게 하면
   * 아무도 안 한다 -- 대충 몇 곳만 넣고 말거나, 계획 자체를 안 만든다.
   *
   * 참조가 아니라 복사다. 들여온 뒤에는 따로 움직인다. 두 계획이 늘 같아야
   * 한다면 경로를 계획 밖으로 꺼내 공유해야 하는데, 그건 한쪽을 고치면 다른
   * 쪽이 말없이 달라진다는 뜻이기도 하다. 지금은 그 값을 치를 이유가 없다.
   */
  const importRoute = (plan, route) => {
    const incoming = {
      name: route.name || plan.name,
      stops: route.stops.map((st) => ({
        facilityId: st.facilityId,
        sectionId: st.sectionId ?? null,
        templateId: st.templateId ?? null,
      })),
    };
    set({ routes: [...form.routes.filter((r) => !isBlankRoute(r)), incoming] });
    setImportOpen(false);
  };

  /**
   * 들를 곳의 자리를 옮긴다.
   *
   * 저장할 때 배열 순서를 그대로 order 로 적으므로 여기서만 바꾸면 된다.
   */
  const moveStop = (ri, from, to) => {
    const stops = form.routes[ri].stops;
    if (to < 0 || to >= stops.length || from === to) return;
    const next = [...stops];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    setRoute(ri, { stops: next });
  };

  const setStop = (ri, si, patch) =>
    setRoute(ri, {
      stops: form.routes[ri].stops.map((st, i) => (i === si ? { ...st, ...patch } : st)),
    });

  const toggleDay = (value) =>
    set({
      days: form.days.includes(value)
        ? form.days.filter((d) => d !== value)
        : [...form.days, value],
    });

  // 저장하기 전에 이 설정이 몇 회가 되는지 보여준다. '주 1회' 라고 적었는데
  // 실제로는 2회가 되는 순간을 알아채야 한다.
  const routeCount = Math.max(1, form.routes.length);
  // 한 주기가 빚지는 회차 수. 시간대에 적은 '회' 를 더한 것이 전부다.
  const perRoute = form.windows.reduce((n, w) => n + (Number(w.count) || 1), 0);
  const perPeriod =
    form.poolMode === 'PER_ROUTE' ? perRoute * routeCount : perRoute;
  const perWeek =
    form.period === 'DAY'
      ? perPeriod * Math.max(1, form.days.length)
      : form.period === 'WEEK'
        ? perPeriod
        : null;

  // 이름을 안 붙인 경로도 차례에는 나와야 한다.
  const routeNames = form.routes.map((r, i) => (r.name || '').trim() || `경로 ${i + 1}`);

  // 자기 자신은 뺀다. 같은 계획 안에서 경로를 복제하는 것은 다른 이야기다.
  const importable = plans
    .filter((p) => String(p.id) !== String(id))
    .flatMap((p) =>
      (p.routes || [])
        .filter((r) => (r.stops || []).length > 0)
        .map((r) => ({ plan: p, route: r, key: `${p.id}-${r.id}` })),
    );

  const sectionsOf = (facilityId) =>
    facilities.find((f) => String(f.id) === String(facilityId))?.sections || [];

  const save = async () => {
    if (!form.name.trim()) {
      showAlert('알림', '계획 이름을 입력해주세요.', 'warning');
      return;
    }
    if (form.routes.length === 0) {
      showAlert('알림', '순회 경로를 하나 이상 추가해주세요.', 'warning');
      return;
    }
    if (form.routes.some((r) => r.stops.length === 0)) {
      showAlert('알림', '들를 곳이 없는 경로가 있습니다.', 'warning');
      return;
    }
    if (form.routes.some((r) => r.stops.some((st) => !st.facilityId))) {
      showAlert('알림', '시설을 고르지 않은 곳이 있습니다.', 'warning');
      return;
    }
    // 시각을 한쪽만 적으면 시간대가 성립하지 않는다.
    const halfFilled = form.windows.find((w) => Boolean(w.start) !== Boolean(w.end));
    if (halfFilled) {
      showAlert('알림', '시간대의 시작과 끝을 모두 입력하거나 모두 비워주세요.', 'warning');
      return;
    }

    setSaving(true);
    try {
      const body = {
        name: form.name.trim(),
        period: form.period,
        // 목록·매트릭스가 계획의 횟수를 이 값으로 읽는다. 시간대에서 센 값을
        // 그대로 넣어야 화면마다 다른 숫자를 말하지 않는다.
        quota: perRoute,
        windows: form.windows.map((w) => ({
          label: w.label,
          count: Number(w.count) || 1,
          // 비운 이름은 아예 보내지 않는다. 빈 문자열을 이름으로 새기면 회차가
          // 이름 없이 이름 있는 척한다.
          ...(w.name?.trim() ? { name: w.name.trim() } : {}),
          ...(w.start && w.end ? { start: w.start, end: w.end } : {}),
        })),
        dayRule: form.days.length > 0 ? { days: form.days } : null,
        poolMode: form.poolMode,
        graceMinutes: Number(form.graceMinutes) || 60,
        status: form.status,
        assignedWorkerIds: form.assignedWorkerIds,
        routes: form.routes.map((r, ri) => ({
          name: r.name.trim() || null,
          order: ri,
          stops: r.stops.map((st, si) => ({
            facilityId: Number(st.facilityId),
            sectionId: st.sectionId ? Number(st.sectionId) : null,
            templateId: st.templateId ? Number(st.templateId) : null,
            order: si,
          })),
        })),
      };

      if (isNew) await api.post('/inspection-plans', body);
      else await api.patch(`/inspection-plans/${id}`, body);

      navigate('/inspection-plans', { replace: true });
    } catch (err) {
      console.error(err);
      showAlert('오류', '저장하지 못했습니다: ' + (err.response?.data?.message || err.message), 'danger');
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    const ok = await showConfirm(
      '계획 삭제',
      '이 계획을 지울까요? 이미 만들어진 점검 기록은 남습니다.',
      'danger',
    );
    if (!ok) return;
    try {
      await api.delete(`/inspection-plans/${id}`);
      navigate('/inspection-plans', { replace: true });
    } catch (err) {
      showAlert('오류', '삭제하지 못했습니다.', 'danger');
    }
  };

  if (loading) {
    return (
      <div className="text-center p-5">
        <div className="spinner-border text-primary" role="status"></div>
      </div>
    );
  }

  return (
    <div className="pb-5">
      <div className="page-title-bar">
        <button className="btn border-0 p-0 me-2 text-dark" onClick={() => navigate(-1)}>
          <i className="bi bi-chevron-left fs-5"></i>
        </button>
        <h5 className="fw-bold m-0">{isNew ? '새 점검 계획' : '점검 계획'}</h5>
      </div>

      <div className="bg-white rounded-4 border p-3 mb-3">
        <label className="text-muted small mb-1">계획 이름</label>
        <input
          className="form-control mb-3"
          value={form.name}
          onChange={(e) => set({ name: e.target.value })}
          placeholder="예: 선흥빌딩 정기순회"
        />

        <div className="fw-bold mb-2">언제, 얼마나</div>
        <div className="d-flex align-items-center gap-2 mb-3 flex-wrap">
          <select
            className="form-select w-auto"
            value={form.period}
            onChange={(e) => set({ period: e.target.value })}
          >
            {PERIODS.map((p) => (
              <option key={p.value} value={p.value}>{p.label}</option>
            ))}
          </select>
          {/* 횟수를 따로 적는 칸이 있었는데 그 숫자는 아무 일도 하지 않았다.
              회차는 시간대의 '회' 값 합계로만 만들어진다. 같은 것을 두 군데서
              말하게 두면 언젠가 서로 어긋나고, 그때 화면이 거짓말을 한다. */}
          <span className="text-muted small">마다, 아래 시간대에 적은 만큼</span>
        </div>

        <div className="text-muted small mb-1">시간대</div>
        {form.windows.map((w, i) => (
          <div key={i} className="d-flex align-items-center gap-2 mb-2 flex-wrap">
            {/* 라벨은 '언제 해야 하는가' 를 정한다. 부르는 말까지 겸하게 두면
                시간 무관 셋이 나란히 있을 때 어느 줄인지 알 수가 없다. */}
            <input
              type="text"
              className="form-control"
              style={{ width: '110px' }}
              placeholder="이름"
              value={w.name ?? ''}
              onChange={(e) => setWindow(i, { name: e.target.value })}
            />
            <select
              className="form-select"
              style={{ width: '120px' }}
              value={w.label}
              onChange={(e) =>
                setWindow(
                  i,
                  // 시간 무관인데 시각이 남아 있으면 그 시각이 이깁니다 -- 이름과
                  // 실제가 어긋나므로 같이 비운다.
                  e.target.value === 'ANYTIME'
                    ? { label: 'ANYTIME', start: '', end: '' }
                    : { label: e.target.value },
                )
              }
            >
              {WINDOW_LABELS.map((l) => (
                <option key={l.value} value={l.value}>{l.label}</option>
              ))}
              {/* 목록에 없는 옛 값이 저장돼 있으면 그대로 둔다. 말없이 다른
                  값으로 바꾸면 계획이 조용히 달라진다. */}
              {!WINDOW_LABELS.some((l) => l.value === w.label) && (
                <option value={w.label}>{w.label}</option>
              )}
            </select>
            {w.label !== 'ANYTIME' && (
              <>
                <input
                  type="time"
                  className="form-control"
                  style={{ width: '120px' }}
                  value={w.start}
                  onChange={(e) => setWindow(i, { start: e.target.value })}
                />
                <span className="text-muted">~</span>
                <input
                  type="time"
                  className="form-control"
                  style={{ width: '120px' }}
                  value={w.end}
                  onChange={(e) => setWindow(i, { end: e.target.value })}
                />
                {/* 브라우저의 시각 입력은 시·분·오전오후를 각각 지워야 비워진다.
                    한 번에 되돌릴 자리를 둔다. */}
                {(w.start || w.end) && (
                  <button
                    className="btn btn-link p-0 text-muted"
                    title="시각 비우기"
                    onClick={() => setWindow(i, { start: '', end: '' })}
                  >
                    <i className="bi bi-eraser"></i>
                  </button>
                )}
              </>
            )}
            <input
              type="number"
              min="1"
              className="form-control"
              style={{ width: '70px' }}
              value={w.count}
              onChange={(e) => setWindow(i, { count: e.target.value })}
            />
            <span className="text-muted small">회</span>
            {form.windows.length > 1 && (
              <button
                className="btn btn-link text-danger p-0 ms-1"
                onClick={() => set({ windows: form.windows.filter((_, j) => j !== i) })}
              >
                <i className="bi bi-x-lg"></i>
              </button>
            )}
          </div>
        ))}
        {/* 시각을 비워 두면 예전 기본값(오전 06~12 등)으로 동작한다. 회사마다
            오전이 다르므로 실제 시각을 적는 편이 낫다. */}
        <div className="text-muted mb-3" style={{ fontSize: '0.72rem' }}>
          <strong>이름</strong>은 화면에서 부를 말입니다(「1회차」, 「점심 전」). 판정에는 쓰이지 않고,
          비우면 시간대 이름으로 부르되 같은 시간대가 여럿이면 번호가 붙습니다.
          <br />
          시각을 비우면 시간대에 따른 기본 범위(오전 06~12, 오후 12~18, 야간 18~24)로 동작합니다.
          실제 시각을 적으면 조기·지연 판정이 정확해집니다. <strong>시간 무관</strong>은 하루 중 언제 해도 정시입니다.
        </div>
        <button
          className="btn btn-sm btn-white border mb-3"
          onClick={() => set({ windows: [...form.windows, emptyWindow()] })}
        >
          <i className="bi bi-plus"></i> 시간대 추가
        </button>

        <div className="text-muted small mb-1">가능한 요일</div>
        <div className="d-flex gap-1 flex-wrap">
          {DAYS.map((d) => (
            <button
              key={d.value}
              className={`btn btn-sm ${form.days.includes(d.value) ? 'btn-primary' : 'btn-white border'}`}
              style={{ width: '40px' }}
              onClick={() => toggleDay(d.value)}
            >
              {d.label}
            </button>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-4 border p-3 mb-3">
        <div className="fw-bold mb-1">순회 경로</div>
        {/* 한 회차의 뜻을 여기서 못 박는다. 경로 안의 곳은 언제나 전부 돈다 --
            들를 곳을 늘려도 횟수는 늘지 않는다.

            순서는 시스템이 강제하지 않는다. 점검을 짝지을 때 정거장의 순서는
            보지 않으므로 어느 곳부터 해도 된다. '순서대로 적습니다' 라고만
            써 두면 지켜야 하는 규칙으로 읽힌다. */}
        <p className="text-muted small mb-3">
          한 회차에 들를 곳입니다. 경로 안의 곳은 <strong>한 회차에 전부</strong> 돕니다.
          순서는 보기 편하라고 있는 것이고, <strong>실제 점검은 어느 곳부터 해도 됩니다.</strong>
        </p>

        {form.routes.map((route, ri) => (
          <div key={ri} className="border rounded-3 p-2 mb-2">
            <div className="d-flex align-items-center gap-2 mb-2">
              <span className="badge bg-light text-dark border fw-normal">
                경로 {ri + 1}
              </span>
              <input
                className="form-control form-control-sm"
                value={route.name}
                onChange={(e) => setRoute(ri, { name: e.target.value })}
                placeholder={form.routes.length > 1 ? '예: 서초 경로' : '이름 (선택)'}
              />
              {form.routes.length > 1 && (
                <button
                  className="btn btn-link text-danger p-0"
                  onClick={() => set({ routes: form.routes.filter((_, j) => j !== ri) })}
                >
                  <i className="bi bi-x-lg"></i>
                </button>
              )}
            </div>

            {/* 들를 곳 하나가 곧 제출 한 번이다. 이걸 명시하지 않으면 구역을
                쪼갤지 건물 전체로 둘지 판단할 근거가 없다. */}
            <div className="text-muted mb-1" style={{ fontSize: '0.72rem' }}>
              들를 곳 — 한 곳이 점검표 한 장, 제출 한 번입니다
            </div>

            {route.stops.map((st, si) => (
              <div
                key={si}
                className="d-flex flex-wrap gap-1 align-items-center mb-1 rounded-2"
                style={{
                  background:
                    dragging?.ri === ri && dragging.over === si && dragging.si !== si
                      ? '#eef2ff'
                      : 'transparent',
                  opacity: dragging?.ri === ri && dragging.si === si ? 0.4 : 1,
                }}
                onDragOver={(e) => {
                  if (dragging?.ri !== ri) return;
                  e.preventDefault();
                  e.dataTransfer.dropEffect = 'move';
                  setDragging((d) => (d && d.over !== si ? { ...d, over: si } : d));
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  if (dragging?.ri === ri) moveStop(ri, dragging.si, si);
                  setDragging(null);
                }}
              >
                {/* 끌어서 옮기는 손잡이. 마우스에서만 동작하므로 좁은 화면에는
                    화살표를 대신 둔다 -- 계획을 고치는 일이 늘 큰 화면에서
                    일어난다고 볼 수 없다. */}
                <span
                  className="text-muted d-none d-md-inline"
                  draggable={route.stops.length > 1}
                  onDragStart={(e) => {
                    // Firefox 는 dataTransfer 에 값이 없으면 끌기를 시작하지 않는다.
                    e.dataTransfer.effectAllowed = 'move';
                    e.dataTransfer.setData('text/plain', String(si));
                    setDragging({ ri, si, over: si });
                  }}
                  onDragEnd={() => setDragging(null)}
                  style={{ cursor: route.stops.length > 1 ? 'grab' : 'default' }}
                >
                  <i className="bi bi-grip-vertical"></i>
                </span>
                <span className="text-muted text-nowrap" style={{ fontSize: '0.72rem', width: '18px' }}>
                  {si + 1}
                </span>
                {route.stops.length > 1 && (
                  <span className="d-inline-flex d-md-none">
                    <button
                      className="btn btn-link p-0 text-muted"
                      disabled={si === 0}
                      onClick={() => moveStop(ri, si, si - 1)}
                    >
                      <i className="bi bi-chevron-up"></i>
                    </button>
                    <button
                      className="btn btn-link p-0 text-muted ms-1"
                      disabled={si === route.stops.length - 1}
                      onClick={() => moveStop(ri, si, si + 1)}
                    >
                      <i className="bi bi-chevron-down"></i>
                    </button>
                  </span>
                )}
                <select
                  className="form-select form-select-sm"
                  style={{ flex: '1 1 120px' }}
                  value={st.facilityId || ''}
                  onChange={(e) => setStop(ri, si, { facilityId: e.target.value, sectionId: null })}
                >
                  <option value="">시설 선택</option>
                  {facilities.map((f) => (
                    <option key={f.id} value={f.id}>{f.name}</option>
                  ))}
                </select>
                <select
                  className="form-select form-select-sm"
                  style={{ flex: '1 1 110px' }}
                  value={st.sectionId || ''}
                  onChange={(e) => setStop(ri, si, { sectionId: e.target.value || null })}
                >
                  <option value="">건물 전체를 한 장으로</option>
                  {sectionsOf(st.facilityId).map((sec) => (
                    <option key={sec.id} value={sec.id}>{sec.name}</option>
                  ))}
                </select>
                <select
                  className="form-select form-select-sm"
                  style={{ flex: '1 1 130px' }}
                  value={st.templateId || ''}
                  onChange={(e) => setStop(ri, si, { templateId: e.target.value || null })}
                >
                  <option value="">점검표 선택</option>
                  {templates.map((tpl) => (
                    <option key={tpl.id} value={tpl.id}>{tpl.name}</option>
                  ))}
                </select>
                {route.stops.length > 1 && (
                  <button
                    className="btn btn-link text-danger p-0 ms-1"
                    onClick={() =>
                      setRoute(ri, { stops: route.stops.filter((_, j) => j !== si) })
                    }
                  >
                    <i className="bi bi-x-lg"></i>
                  </button>
                )}
              </div>
            ))}

            {/* 점검표가 없으면 근무자가 열었을 때 아무 항목도 없다. 저장은
                막지 않는다 -- 점검표를 아직 안 만들었을 수 있다. */}
            {route.stops.some((st) => st.facilityId && !st.templateId) && (
              <div className="text-danger mt-1" style={{ fontSize: '0.72rem' }}>
                점검표를 고르지 않은 곳이 있습니다. 그곳은 열어도 점검할 항목이 없습니다.
              </div>
            )}

            <button
              className="btn btn-sm btn-white border mt-1"
              onClick={() => setRoute(ri, { stops: [...route.stops, emptyStop()] })}
            >
              <i className="bi bi-plus"></i> 들를 곳 추가
            </button>
          </div>
        ))}

        <div className="d-flex gap-2 flex-wrap">
          <button
            className="btn btn-sm btn-white border"
            onClick={() => set({ routes: [...form.routes, emptyRoute()] })}
          >
            <i className="bi bi-plus"></i> 순회 경로 추가
          </button>
          {importable.length > 0 && (
            <button
              className="btn btn-sm btn-white border"
              onClick={() => setImportOpen(!importOpen)}
            >
              <i className="bi bi-box-arrow-in-down"></i> 다른 계획에서 가져오기
            </button>
          )}
        </div>

        {importOpen && (
          <div className="border rounded-3 p-2 mt-2" style={{ background: '#f9fafb' }}>
            {/* 복사라는 것을 여기서 말해 둔다. 참조라고 오해하면 원본을 고치고
                여기도 바뀐 줄 안다. */}
            <div className="text-muted mb-1" style={{ fontSize: '0.72rem' }}>
              들를 곳을 그대로 복사해 옵니다. 가져온 뒤에는 따로 움직이므로 원본을
              고쳐도 여기는 그대로입니다.
            </div>
            {importable.map(({ plan, route, key }) => (
              <div key={key} className="d-flex align-items-center gap-2 py-2 border-top">
                <div style={{ minWidth: 0 }}>
                  <div className="small text-truncate">{route.name || plan.name}</div>
                  <div className="text-muted text-truncate" style={{ fontSize: '0.72rem' }}>
                    {plan.name} · {route.stops.length}곳
                  </div>
                </div>
                <button
                  className="btn btn-sm btn-primary rounded-pill ms-auto text-nowrap"
                  onClick={() => importRoute(plan, route)}
                >
                  가져오기
                </button>
              </div>
            ))}
          </div>
        )}

        {/* 경로를 나누면 횟수가 그만큼 곱해진다. 한 건물을 한 회차에 도는
            것이라면 경로 하나 안에 들를 곳을 늘리면 된다. */}
        {form.routes.length > 1 && form.poolMode === 'PER_ROUTE' && (
          <div className="alert alert-warning mt-2 mb-0 py-2 px-3" style={{ fontSize: '0.75rem' }}>
            경로가 {form.routes.length}개라 <strong>횟수가 {form.routes.length}배</strong>가 됩니다.
            한 회차에 여러 곳을 도는 것이라면 <strong>경로 하나</strong>에 들를 곳을 늘리세요.
            번갈아 도는 것이라면 아래에서 <strong>번갈아</strong>를 고르세요.
          </div>
        )}
      </div>

      {form.routes.length > 1 && (
        <div className="bg-white rounded-4 border p-3 mb-3">
          <div className="fw-bold mb-1">경로가 여럿일 때</div>
          <p className="text-muted small mb-2">위 횟수를 경로에 어떻게 나눌지</p>
          {POOL_MODES.map((m) => (
            <label key={m.value} className="d-flex gap-2 py-2" style={{ cursor: 'pointer' }}>
              <input
                type="radio"
                className="form-check-input mt-1"
                checked={form.poolMode === m.value}
                onChange={() => set({ poolMode: m.value })}
              />
              <span>
                <span className="d-block">{m.label}</span>
                <span className="text-muted" style={{ fontSize: '0.75rem' }}>
                  {m.hint(routeNames, perRoute)}
                </span>
              </span>
            </label>
          ))}
        </div>
      )}

      <div className="bg-white rounded-4 border p-3 mb-3">
        <div className="fw-bold mb-1">누가 하나</div>
        {/* 아무도 안 고르면 아무나 할 수 있다는 뜻이다. 강제하면 사람이 바뀔
            때마다 계획을 고쳐야 하고, 그러다 아무도 못 하는 날이 생긴다. */}
        <p className="text-muted small mb-2">
          {form.assignedWorkerIds.length === 0
            ? '아무도 고르지 않으면 누구나 할 수 있습니다.'
            : '고른 사람의 「오늘 할 점검」에만 뜹니다.'}
        </p>
        <div className="d-flex flex-wrap gap-2">
          {workers.map((w) => {
            const on = form.assignedWorkerIds.includes(w.id);
            return (
              <button
                key={w.id}
                className={`btn btn-sm rounded-pill ${on ? 'btn-primary' : 'btn-white border'}`}
                onClick={() =>
                  set({
                    assignedWorkerIds: on
                      ? form.assignedWorkerIds.filter((x) => x !== w.id)
                      : [...form.assignedWorkerIds, w.id],
                  })
                }
              >
                {w.name}
              </button>
            );
          })}
        </div>
      </div>

      <div className="bg-white rounded-4 border p-3 mb-3">
        <div className="d-flex align-items-center gap-2 mb-2">
          <span className="text-muted small">상태</span>
          <select
            className="form-select form-select-sm w-auto"
            value={form.status}
            onChange={(e) => set({ status: e.target.value })}
          >
            <option value="ACTIVE">진행</option>
            <option value="PAUSED">중지</option>
            <option value="ENDED">종료</option>
          </select>
          <span className="text-muted small ms-3">유예</span>
          <input
            type="number"
            min="0"
            className="form-control form-control-sm"
            style={{ width: '80px' }}
            value={form.graceMinutes}
            onChange={(e) => set({ graceMinutes: e.target.value })}
          />
          <span className="text-muted small">분</span>
        </div>
      </div>

      {/* 저장 직전에 결과를 보여준다. 막지는 않는다 -- 감당할 수 있는 양인지는
          한 순회에 걸리는 시간과 근무 인원에 달렸고 시스템이 알 수 없다. */}
      <div className="bg-light rounded-3 p-3 d-flex align-items-center justify-content-between flex-wrap gap-2">
        <div>
          <div className="text-muted small">이 설정이면</div>
          <div className="fw-bold">
            {perWeek != null ? `주 ${perWeek}회` : `${form.period === 'MONTH' ? '월' : ''} ${perPeriod}회`}
          </div>
        </div>
        <div className="d-flex gap-2">
          {!isNew && (
            <button className="btn btn-white border text-danger" onClick={remove}>
              삭제
            </button>
          )}
          <button className="btn btn-primary" onClick={save} disabled={saving}>
            {saving ? '저장 중...' : '저장'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default InspectionPlanFormPage;
